import AnnotationsService from '@/domain/Annotations/AnnotationsService';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { hexToRgba, generateColor } from '@/utils/DashboardGraphs';
const annotationService = new AnnotationsService();

export interface UseAnnotationsResult {
	annotationsList: any[];
	setAnnotationsList: Dispatch<SetStateAction<any[]>>;
	showAnnotationModal: boolean;
	setShowAnnotationModal: Dispatch<SetStateAction<boolean>>;
	showAnnotationListModal: boolean;
	setShowAnnotationListModal: Dispatch<SetStateAction<boolean>>;
	annotationListToShow: any[];
	setAnnotationListToShow: Dispatch<SetStateAction<any[]>>;
	quality: number;
	setQuality: Dispatch<SetStateAction<number>>;
	comment: string;
	setComment: Dispatch<SetStateAction<string>>;
	title: string;
	setTitle: Dispatch<SetStateAction<string>>;
	isCreationMode: boolean;
	setIsCreationMode: Dispatch<SetStateAction<boolean>>;
	sourceToAnnotation: any;
	setSourceToAnnotation: Dispatch<SetStateAction<any>>;
	dates: [number, number] | null;
	setDates: Dispatch<SetStateAction<[number, number] | null>>;
	annotationMode: boolean;
	setAnnotationMode: Dispatch<SetStateAction<boolean>>;
	filterAnnotationsByDate: (unixDate: number) => any[];
	checkQualityForNewAnnotation: (
		qualityRange: {start: number, end: number},
	) => string;
	handleGetAnnotations: (
		sourceId: string,
		parameterId: string,
		serialNumber: string,
		isRecommended?: boolean,
	) => Promise<void>;
}

interface useAnnotationsProps {
	readingsList: any[];
	addPlotBandToChart: (annotation: any) => void;
	removeAllPlotBands: () => void;
	removePlotBandsByIds?: (ids: string[]) => void;
	selectedDates: [any, any];
	sourcesByParam?: any[];
	seriesVisibilityState?: Map<string, boolean> | Map<number, boolean>;
}

export const useAnnotations = ({
	readingsList,
	addPlotBandToChart,
	removeAllPlotBands,
	removePlotBandsByIds,
	selectedDates,
	sourcesByParam = [],
	seriesVisibilityState,
}: useAnnotationsProps): UseAnnotationsResult => {
	const [annotationsList, setAnnotationsList] = useState<any[]>([]);
	const [showAnnotationModal, setShowAnnotationModal] = useState<boolean>(false);
	const [showAnnotationListModal, setShowAnnotationListModal] = useState<boolean>(false);
	const [annotationListToShow, setAnnotationListToShow] = useState<any[]>([]);
	const [quality, setQuality] = useState<number>(0);
	const [comment, setComment] = useState<string>('');
	const [title, setTitle] = useState<string>('');
	const [isCreationMode, setIsCreationMode] = useState<boolean>(true);
	const [sourceToAnnotation, setSourceToAnnotation] = useState<any>(null);
	const [dates, setDates] = useState<[number, number] | null>(null);
	const [annotationMode, setAnnotationMode] = useState<boolean>(false);

	const filterAnnotationsByDate = useCallback(
		(unixDate: number) => {
			const dateToCompare = dayjs.unix(Math.floor(unixDate / 1000));

			return annotationsList.filter((annotation: any) => {
				const startDate = dayjs(annotation.startDate);
				const endDate = dayjs(annotation.endDate);
				return dateToCompare.isAfter(startDate) && dateToCompare.isBefore(endDate);
			});
		},
		[annotationsList],
	);

	const checkQualityForNewAnnotation = useCallback(
		(qualityRange: {start: number, end: number}): string => {
			const { start, end } = qualityRange;
			if (start === end) {
				return `${start}`;
			}
			if (start !== end) {
				return `${start} - ${end}`;
			}
			return '10';
		},
		[],
	);

	async function handleGetAnnotations(
		sourceId: string,
		parameterId: string,
		serialNumber: string,
		isRecommended: boolean = false,
	) {
		const [dateFrom, dataTo] = selectedDates;
		const response = await annotationService.getAnnotationsBySourceId(
			parameterId,
			sourceId,
			dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
			dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
		);

		if (response) {
			let annotations = response;

			// Filtrar según tipo de source
			if (isRecommended) {
				// Para sources RECOMENDADOS: INCLUIR SOLO notes (quality === null)
				// Las notes se muestran en todos los sources, pero se cargan desde el recomendado
				annotations = annotations.filter(
					(annotation: any) => annotation.quality === null || annotation.quality === undefined
				);
			} else {
				// Para sources NO RECOMENDADOS: INCLUIR notes Y anotaciones de calidad
				// Notes: se muestran en todos los sources (quality === null)
				// Anotaciones de calidad: solo en su source específico (quality !== null)
				// NO FILTRAR NADA - mostrar todo lo que venga del API
			}

			// Agregar anotaciones a la lista, evitando duplicados
			setAnnotationsList((prev: any[]) => {
				const combined = [...prev, ...annotations];
				const withoutDuplicates = combined.filter(
					(objeto, indice, self) =>
						indice === self.findIndex((o) => o.annotationId === objeto.annotationId),
				);

				// Agrupar notes por groupId ANTES de agregar al estado
				const grouped = groupNotesByGroupId(withoutDuplicates);

				// Agregar plotbands solo para las nuevas anotaciones
				annotations.forEach((annotation: any) => {
					addPlotBandToChart(annotation);
				});

				return grouped;
			});
		}
	}

	// Limpiar anotaciones cuando readingsList está vacío (todos los sources desactivados o parámetro deseleccionado)
	// Track loaded parameters to manage annotations incrementally
	const loadedParametersRef = useRef<Set<string>>(new Set());

	// Función helper para agrupar notes por groupId
	const groupNotesByGroupId = useCallback((annotations: any[]) => {
		// Separar notes (con groupId) de annotations regulares (sin groupId)
		const notes = annotations.filter((a: any) => a.groupId);
		const regularAnnotations = annotations.filter((a: any) => !a.groupId);

		// Si no hay notes, retornar tal cual
		if (notes.length === 0) {
			return annotations;
		}

		// Agrupar notes por groupId
		const notesByGroupId = new Map<string, any[]>();
		notes.forEach((note: any) => {
			if (!notesByGroupId.has(note.groupId)) {
				notesByGroupId.set(note.groupId, []);
			}
			notesByGroupId.get(note.groupId)!.push(note);
		});

		// Crear notes agrupadas con parameterId como array
		const groupedNotes: any[] = [];
		notesByGroupId.forEach((notesInGroup, groupId) => {
			if (notesInGroup.length === 1) {
				// Solo una note en el grupo, mantener como está
				groupedNotes.push(notesInGroup[0]);
			} else {
				// Múltiples notes con el mismo groupId, combinar parameterId
				const firstNote = notesInGroup[0];

				// Recolectar todos los parameterId y aplanar arrays
				const allParameterIds: number[] = [];
				notesInGroup.forEach(n => {
					if (Array.isArray(n.parameterId)) {
						// Si ya es un array, agregar cada elemento
						n.parameterId.forEach((pid: number) => {
							if (!allParameterIds.includes(pid)) {
								allParameterIds.push(pid);
							}
						});
					} else {
						// Si es un número, agregarlo si no existe
						if (!allParameterIds.includes(n.parameterId)) {
							allParameterIds.push(n.parameterId);
						}
					}
				});

				groupedNotes.push({
					...firstNote,
					parameterId: allParameterIds, // Array de parameterIds únicos
				});
			}
		});

		const result = [...regularAnnotations, ...groupedNotes];

		return result;
	}, []);

	useEffect(() => {
		if (!readingsList || readingsList.length === 0) {
			setAnnotationsList((prev: any[]) => {
				if (prev.length > 0 && removePlotBandsByIds) {
					const idsToRemove = prev.map((annotation: any) => 
						annotation.groupId ?? `annotation-${annotation.annotationId}`
					);
					removePlotBandsByIds(idsToRemove);
				}
				return [];
			});
			removeAllPlotBands();
			loadedParametersRef.current.clear();
			return;
		}

		// Wait for sourcesByParam to be populated before loading annotations
		if (!sourcesByParam || sourcesByParam.length === 0) {
			return;
		}

		// Filter readingsList to only include visible items
		// IMPORTANTE: Para notes, debemos mantenerlas si HAY ALGÚN source activo del parámetro
		// No solo basarnos en seriesVisibilityState del parámetro
		const visibleReadings = readingsList.filter((item: any) => {
			const parameterId = item.originalParameterId;

			// Buscar si hay algún source activo para este parámetro en sourcesByParam
			const paramSources = sourcesByParam?.find((p: any) => p.id === parameterId);

			if (paramSources && paramSources.sources) {
				// Verificar si hay al menos un source checked (activo)
				const hasActiveSource = paramSources.sources.some((source: any) => source.checked === true);
				return hasActiveSource;
			}

			// Fallback: usar seriesVisibilityState si no hay sourcesByParam
			if (!seriesVisibilityState) return true;
			const isVisible = (seriesVisibilityState as any).get(parameterId) ?? 
							  (seriesVisibilityState as any).get(String(parameterId)) ?? 
							  true;
			return isVisible;
		});

		// Create set of current parameter keys (only visible ones)
		const currentKeys = new Set(
			visibleReadings.map((item: any) => 
				`${item.originalParameterId}-${item.originalSourceSerialNumber || 'recommended'}`
			)
		);

		// Get current parameter IDs (only visible ones)
		const currentParameterIds = new Set(
			visibleReadings.map((item: any) => item.originalParameterId)
		);

		// Find keys to add (new parameters/sources)
		const keysToAdd = Array.from(currentKeys).filter(key => !loadedParametersRef.current.has(key));

		// Find keys to remove (deselected parameters/sources)
		const keysToRemove = Array.from(loadedParametersRef.current).filter(key => !currentKeys.has(key));

		// CASO ESPECIAL: Si no hay visibleReadings pero hay anotaciones, limpiar todo
		if (visibleReadings.length === 0) {
			setAnnotationsList((prev: any[]) => {
				if (prev.length > 0) {
					if (removePlotBandsByIds) {
						const idsToRemove = prev.map((annotation: any) => 
							annotation.groupId ?? `annotation-${annotation.annotationId}`
						);
						removePlotBandsByIds(idsToRemove);
					}
				}
				return [];
			});
			loadedParametersRef.current.clear();
			return; // Salir temprano, no hay nada más que hacer
		}

		// Remove annotations for deselected parameters
		if (keysToRemove.length > 0) {
			const parameterIdsToRemove = new Set(
				keysToRemove.map(key => parseInt(key.split('-')[0]))
			);

			// Filter out annotations that belong ONLY to removed parameters
			setAnnotationsList((prev: any[]) => {
				const filtered = prev.filter((annotation: any) => {
					// Keep annotation if it's associated with at least one active parameter
					if (Array.isArray(annotation.parameterId)) {
						// Note with multiple parameters - keep if ANY parameter is still active
						const hasActiveParam = annotation.parameterId.some((pid: any) => 
							currentParameterIds.has(parseInt(pid))
						);
						return hasActiveParam;
					} else {
						// Single parameter annotation - keep if parameter is still active
						const shouldKeep = currentParameterIds.has(annotation.parameterId);
						return shouldKeep;
					}
				});

				// Remove plotbands for annotations that were filtered out
				const removedAnnotations = prev.filter(a => !filtered.includes(a));
				if (removedAnnotations.length > 0 && removePlotBandsByIds) {
					const idsToRemove = removedAnnotations.map((annotation: any) => 
						annotation.groupId ?? `annotation-${annotation.annotationId}`
					);
					removePlotBandsByIds(idsToRemove);
				}

				return filtered;
			});

			// Update loaded parameters
			keysToRemove.forEach(key => loadedParametersRef.current.delete(key));
		}

		// Add annotations for new parameters
		if (keysToAdd.length > 0) {
			keysToAdd.forEach(key => {
				loadedParametersRef.current.add(key);

				// Parse the key to get parameter and source info
				// Key format: "parameterId-sourceSerialNumber" or "parameterId-recommended"
				const parts = key.split('-');
				const parameterIdStr = parts[0];
				const sourceInfo = parts.slice(1).join('-'); // Rejoin in case serial number has dashes
				const parameterId = parseInt(parameterIdStr);

				const item = readingsList.find((r: any) => {
					const match = r.originalParameterId === parameterId && 
						(r.originalSourceSerialNumber || 'recommended') === sourceInfo;
					return match;
				});

				if (!item) {
					return;
				}

				const isRecommendedSource =
					item.originalSourceSerialNumber === null || item.originalSourceSerialNumber === undefined;

				if (isRecommendedSource) {
					const paramSources = sourcesByParam?.find(
						(param: any) => param.id === item.originalParameterId,
					);

					if (paramSources && paramSources.sources) {
						const firstRealSource = paramSources.sources.find(
							(source: any) => source.name !== 'Recommended' && source.sourceId !== null,
						);

						if (firstRealSource) {
							handleGetAnnotations(
								firstRealSource.sourceId?.toString() || '',
								item.originalParameterId,
								firstRealSource.serialNumber,
								true, // isRecommended = true para filtrar notes
							);
						} else {
							// Remover la key de loadedParametersRef para que se vuelva a intentar
							loadedParametersRef.current.delete(key);
						}
					} else {
						// Remover la key de loadedParametersRef para que se vuelva a intentar
						loadedParametersRef.current.delete(key);
					}
				} else if (item.originalSourceSerialNumber) {
					handleGetAnnotations(
						item.originalSourceId,
						item.originalParameterId,
						item.originalSourceSerialNumber,
						false,
					);
				}
			});
		}
	}, [readingsList, sourcesByParam, seriesVisibilityState]);

	return {
		annotationsList,
		setAnnotationsList,
		showAnnotationModal,
		setShowAnnotationModal,
		showAnnotationListModal,
		setShowAnnotationListModal,
		annotationListToShow,
		setAnnotationListToShow,
		quality,
		setQuality,
		comment,
		setComment,
		title,
		setTitle,
		isCreationMode,
		setIsCreationMode,
		sourceToAnnotation,
		setSourceToAnnotation,
		dates,
		setDates,
		annotationMode,
		setAnnotationMode,
		filterAnnotationsByDate,
		checkQualityForNewAnnotation,
		handleGetAnnotations,
	};
};
